import React, { useEffect, useState } from "react";
import { callAPI } from "../../utils/apiUtils";
import { apiUrls } from "../../utils/apiUrls";
import { toast } from "react-toastify";

const ToggleSwitch = ({ checked, onChange, large }) => (
  <label className={`toggleSwitch${large ? " toggleSwitch--lg" : ""}`}>
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span className="toggleSlider" />
  </label>
);

/**
 * Mirrors the admin's Mqttprinter Management page (same backend model methods, see
 * v3/mqtt-printers/*) — status/primary toggles, docket version, category/subcategory
 * assignment, and per-role print settings are all editable here, same as admin.
 *
 * "Clear bin" is intentionally not included — the admin's version shells out to `mosquitto_pub`
 * directly from the admin server using local MQTT broker credentials, a capability this app
 * doesn't have.
 */
const PrintersPanel = () => {
  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [detailsById, setDetailsById] = useState({});
  const [detailsLoadingId, setDetailsLoadingId] = useState(null);
  const [docketVersions, setDocketVersions] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [itemSearchTerm, setItemSearchTerm] = useState({});
  const [itemSearchResults, setItemSearchResults] = useState({});
  const [itemSearchLoading, setItemSearchLoading] = useState(null);
  const [localIpInputs, setLocalIpInputs] = useState({});
  const searchDebounceRef = React.useRef({});

  useEffect(() => {
    const fetchPrinters = async () => {
      setLoading(true);
      try {
        const res = await callAPI(apiUrls.MQTT_PRINTERS_LIST, {}, "GET");
        setPrinters(res.data?.data || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load printers");
      } finally {
        setLoading(false);
      }
    };
    fetchPrinters();

    callAPI(apiUrls.MQTT_PRINTER_MASTER_DOCKET_VERSIONS, {}, "GET")
      .then((res) => setDocketVersions(res.data?.data || []))
      .catch((err) => console.error(err));

    callAPI(apiUrls.MQTT_PRINTER_CATEGORIES, {}, "GET")
      .then((res) => setAllCategories(res.data?.data || []))
      .catch((err) => console.error(err));
  }, []);

  const updatePrinterInList = (printerId, changes) => {
    setPrinters((prev) => prev.map((p) => (p.id === printerId ? { ...p, ...changes } : p)));
  };

  const toggleExpand = async (printerId) => {
    if (expandedId === printerId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(printerId);

    if (!detailsById[printerId]) {
      await refetchDetails(printerId);
    }
  };

  const refetchDetails = async (printerId) => {
    setDetailsLoadingId(printerId);
    try {
      const res = await callAPI(apiUrls.MQTT_PRINTER_DETAILS, { id: printerId }, "GET");
      setDetailsById((prev) => ({ ...prev, [printerId]: res.data?.data || null }));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load printer details");
    } finally {
      setDetailsLoadingId(null);
    }
  };

  const handleToggleStatus = async (printer) => {
    const newStatus = Number(printer.status) === 1 ? 0 : 1;
    updatePrinterInList(printer.id, { status: newStatus });
    try {
      await callAPI(
        apiUrls.MQTT_PRINTER_UPDATE_STATUS,
        {},
        "POST",
        { id: printer.id, status: newStatus },
        {}
      );
      toast.success(newStatus ? "Printer activated" : "Printer deactivated");
    } catch (err) {
      console.error(err);
      updatePrinterInList(printer.id, { status: printer.status });
      toast.error("Failed to update status");
    }
  };

  const handleTogglePrimary = async (printer) => {
    if (Number(printer.primary) !== 1 && !window.confirm("Are you sure you want to set this as the primary printer?")) {
      return;
    }
    const newPrimary = Number(printer.primary) === 1 ? 0 : 1;
    updatePrinterInList(printer.id, { primary: newPrimary });
    try {
      await callAPI(apiUrls.MQTT_PRINTER_UPDATE_PRIMARY, {}, "POST", { id: printer.id }, {});
      toast.success("Primary printer updated");
    } catch (err) {
      console.error(err);
      updatePrinterInList(printer.id, { primary: printer.primary });
      toast.error("Failed to update primary printer");
    }
  };

  const handleDocketVersionChange = async (printer, docketVersionId) => {
    try {
      await callAPI(
        apiUrls.MQTT_PRINTER_UPDATE_DOCKET_VERSION,
        {},
        "POST",
        { id: printer.id, docket_version_id: docketVersionId },
        {}
      );
      const version = docketVersions.find((v) => String(v.id) === String(docketVersionId));
      updatePrinterInList(printer.id, {
        docket_version_id: docketVersionId,
        docket_version: version ? `${version.version} - ${version.description}` : null,
      });
      toast.success("Docket version updated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update docket version");
    }
  };

  const handleLocalIpChange = async (printer, newIp) => {
    const trimmed = newIp.trim();
    if (trimmed === (printer.local_ip || "")) {
      return;
    }
    try {
      await callAPI(
        apiUrls.MQTT_PRINTER_UPDATE_LOCAL_IP,
        {},
        "POST",
        { id: printer.id, local_ip: trimmed },
        {}
      );
      updatePrinterInList(printer.id, { local_ip: trimmed });
      setLocalIpInputs((prev) => ({ ...prev, [printer.id]: trimmed }));
      toast.success("Local IP updated");
    } catch (err) {
      console.error(err);
      setLocalIpInputs((prev) => ({ ...prev, [printer.id]: printer.local_ip || "" }));
      toast.error("Failed to update local IP — check the address is valid");
    }
  };

  const handleRoleChange = async (printerId, role, changes) => {
    setDetailsById((prev) => {
      const current = prev[printerId];
      if (!current) return prev;
      return {
        ...prev,
        [printerId]: {
          ...current,
          roles: current.roles.map((r) => (r.id === role.id ? { ...r, ...changes } : r)),
        },
      };
    });

    try {
      await callAPI(
        apiUrls.MQTT_PRINTER_UPDATE_ROLES,
        {},
        "POST",
        {
          id: printerId,
          roles: [{ master_printer_role_id: role.id, ...changes }],
        },
        {}
      );
    } catch (err) {
      console.error(err);
      toast.error("Failed to update print setting");
      refetchDetails(printerId);
    }
  };

  const isCategoryLinked = (details, categoryId) =>
    (details?.categories || []).some((c) => c.id === categoryId);

  const isSubcategoryLinked = (details, categoryId, subcategoryId) =>
    (details?.categories || []).some(
      (c) => c.id === categoryId && (c.subcategories || []).some((s) => s.id === subcategoryId)
    );

  const handleToggleCategory = async (printerId, categoryId) => {
    const details = detailsById[printerId];
    const linked = isCategoryLinked(details, categoryId);

    try {
      await callAPI(
        apiUrls.MQTT_PRINTER_LINK_UNLINK_CATEGORIES,
        {},
        "POST",
        {
          id: printerId,
          categories: [categoryId],
          type: "category",
          action: linked ? "unlink" : "link",
        },
        {}
      );
      await refetchDetails(printerId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update category");
    }
  };

  const handleToggleSubcategory = async (printerId, categoryId, subcategoryId) => {
    const details = detailsById[printerId];
    const linked = isSubcategoryLinked(details, categoryId, subcategoryId);

    try {
      await callAPI(
        apiUrls.MQTT_PRINTER_LINK_UNLINK_CATEGORIES,
        {},
        "POST",
        {
          id: printerId,
          categories: [{ category_id: categoryId, subcategory_id: subcategoryId }],
          type: "subcategory",
          action: linked ? "unlink" : "link",
        },
        {}
      );
      await refetchDetails(printerId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update subcategory");
    }
  };

  const handleItemSearchChange = (printerId, term) => {
    setItemSearchTerm((prev) => ({ ...prev, [printerId]: term }));

    if (searchDebounceRef.current[printerId]) {
      clearTimeout(searchDebounceRef.current[printerId]);
    }

    if (!term.trim()) {
      setItemSearchResults((prev) => ({ ...prev, [printerId]: [] }));
      return;
    }

    searchDebounceRef.current[printerId] = setTimeout(async () => {
      setItemSearchLoading(printerId);
      try {
        const res = await callAPI(
          apiUrls.MQTT_PRINTER_SEARCH_ADHOC_ITEMS,
          {},
          "POST",
          { id: printerId, term },
          {}
        );
        setItemSearchResults((prev) => ({ ...prev, [printerId]: res.data?.data || [] }));
      } catch (err) {
        console.error(err);
        toast.error("Failed to search items");
      } finally {
        setItemSearchLoading(null);
      }
    }, 300);
  };

  const handleAssignItem = async (printerId, item) => {
    try {
      await callAPI(
        apiUrls.MQTT_PRINTER_UPDATE_ITEM_ASSIGNMENT,
        {},
        "POST",
        { id: printerId, itemid: item.itemid, is_assigned: 1 },
        {}
      );
      toast.success(`${item.itemname} pinned to this printer`);
      setItemSearchTerm((prev) => ({ ...prev, [printerId]: "" }));
      setItemSearchResults((prev) => ({ ...prev, [printerId]: [] }));
      await refetchDetails(printerId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to assign item");
    }
  };

  const handleUnassignItem = async (printerId, item) => {
    try {
      await callAPI(
        apiUrls.MQTT_PRINTER_UPDATE_ITEM_ASSIGNMENT,
        {},
        "POST",
        { id: printerId, itemid: item.itemid, is_assigned: 0 },
        {}
      );
      await refetchDetails(printerId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to remove item");
    }
  };

  if (loading) {
    return <p>Loading printers...</p>;
  }

  if (printers.length === 0) {
    return <p>No printers configured for this restaurant.</p>;
  }

  return (
    <div className="d-flex flex-column gap-3">
      {printers.map((printer) => {
        const isOnline = Number(printer.online_status) === 1;
        const isActive = Number(printer.status) === 1;
        const isPrimary = Number(printer.primary) === 1;
        const isExpanded = expandedId === printer.id;
        const details = detailsById[printer.id];

        return (
          <div key={printer.id} className="cardStyle printerCard">
            <div className="printerCardHeader">
              <button
                type="button"
                className="printerCardHeaderMain"
                onClick={() => toggleExpand(printer.id)}
              >
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <span className={`printerStatusDot${isOnline ? " online" : ""}`} />
                    <strong>{printer.title || printer.printer_topic}</strong>
                  </div>
                  <div style={{ fontSize: "12px", opacity: 0.7 }}>
                    {printer.printer_topic}
                  </div>
                </div>
              </button>

              <div className="d-flex flex-wrap align-items-center gap-4" style={{ fontSize: "13px" }}>
                <div className="d-flex align-items-center gap-2">
                  <span>Status</span>
                  <ToggleSwitch checked={isActive} onChange={() => handleToggleStatus(printer)} />
                </div>

                <div className="d-flex align-items-center gap-2">
                  <span>Primary</span>
                  <ToggleSwitch checked={isPrimary} onChange={() => handleTogglePrimary(printer)} />
                </div>

                <div className="d-flex align-items-center gap-2">
                  <span>Local IP</span>
                  <input
                    type="text"
                    className="form-control form-control-sm printerLocalIpInput"
                    style={{ width: "130px" }}
                    placeholder="192.168.1.50"
                    title="Address used to reach this printer directly when the POS is running offline"
                    value={localIpInputs[printer.id] ?? printer.local_ip ?? ""}
                    onChange={(e) =>
                      setLocalIpInputs((prev) => ({ ...prev, [printer.id]: e.target.value }))
                    }
                    onBlur={(e) => handleLocalIpChange(printer, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.target.blur();
                      }
                    }}
                  />
                </div>

                <span>
                  <strong style={{ color: isOnline ? "#28a745" : "#dc3545" }}>
                    {isOnline ? "Connected" : "Disconnected"}
                  </strong>
                  {!isOnline && printer.online_status_update_at && (
                    <span style={{ opacity: 0.7 }}>
                      {" "}(last seen {new Date(printer.online_status_update_at).toLocaleString()})
                    </span>
                  )}
                </span>

                <button type="button" className="btn btn-sm printerClearBinBtn" disabled title="Not available: this requires MQTT broker access this app doesn't have">
                  Clear bin
                </button>

                <select
                  className="form-select form-select-sm printerDocketSelect"
                  value={printer.docket_version_id || ""}
                  onChange={(e) => handleDocketVersionChange(printer, e.target.value)}
                >
                  {docketVersions.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.version} - {v.description}
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  className={`printerChevron${isExpanded ? " open" : ""}`}
                  onClick={() => toggleExpand(printer.id)}
                >
                  ▾
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="printerCardBody">
                {detailsLoadingId === printer.id ? (
                  <p>Loading details...</p>
                ) : details ? (
                  <>
                    <div className="d-flex flex-wrap gap-4 mb-3">
                      <div style={{ flex: "2", minWidth: "260px" }}>
                        <div className="printerSectionLabel">Categories</div>
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {allCategories.map((cat) => (
                            <label key={cat.id} className="printerCategoryChip">
                              <input
                                type="checkbox"
                                checked={isCategoryLinked(details, cat.id)}
                                onChange={() => handleToggleCategory(printer.id, cat.id)}
                              />
                              {cat.name}
                            </label>
                          ))}
                        </div>

                        {allCategories
                          .filter((cat) => isCategoryLinked(details, cat.id) && cat.subcategories?.length > 0)
                          .map((cat) => (
                            <div key={cat.id} className="mb-2">
                              <div style={{ fontSize: "12px", opacity: 0.7, marginBottom: "4px" }}>
                                {cat.name} subcategories
                              </div>
                              <div className="d-flex flex-wrap gap-2">
                                {cat.subcategories.map((sub) => (
                                  <label key={sub.id} className="printerCategoryChip printerSubcategoryChip">
                                    <input
                                      type="checkbox"
                                      checked={isSubcategoryLinked(details, cat.id, sub.id)}
                                      onChange={() => handleToggleSubcategory(printer.id, cat.id, sub.id)}
                                    />
                                    {sub.name}
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                      </div>

                      <div style={{ flex: "1", minWidth: "220px" }}>
                        <div className="printerSectionLabel">Ad hoc Items</div>
                        <input
                          type="text"
                          className="printerItemSearchInput"
                          placeholder="Search item to add..."
                          value={itemSearchTerm[printer.id] || ""}
                          onChange={(e) => handleItemSearchChange(printer.id, e.target.value)}
                        />
                        {itemSearchLoading === printer.id && (
                          <div style={{ fontSize: "12px", opacity: 0.7 }}>Searching...</div>
                        )}
                        {(itemSearchResults[printer.id] || []).length > 0 && (
                          <div className="printerItemSearchResults">
                            {itemSearchResults[printer.id].map((item) => (
                              <button
                                key={item.itemid}
                                type="button"
                                className="printerItemSearchResult"
                                onClick={() => handleAssignItem(printer.id, item)}
                              >
                                {item.itemname}
                                {item.category_name && (
                                  <span style={{ opacity: 0.6, fontSize: "11px" }}>
                                    {" "}({item.subcategory_name || item.category_name})
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}

                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {(details.items || []).map((item) => (
                            <span key={item.itemid} className="printerCategoryChip">
                              {item.itemname}
                              <button
                                type="button"
                                className="printerItemRemoveBtn"
                                onClick={() => handleUnassignItem(printer.id, item)}
                                title="Remove"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          {(!details.items || details.items.length === 0) && (
                            <span style={{ fontSize: "12px", opacity: 0.6 }}>No items pinned.</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="printerSectionLabel">Print Settings</div>
                      <div className="printerRolesGrid">
                        {(details.roles || []).map((role) => (
                          <div key={role.id} className="printerRoleCard">
                            <strong>{role.role_name}</strong>
                            <div className="d-flex justify-content-between align-items-center mt-2" style={{ fontSize: "12px" }}>
                              <span className="d-flex align-items-center gap-2">
                                Count:
                                <input
                                  type="number"
                                  min={1}
                                  className="printerCountInput"
                                  value={role.number_of_print ?? 1}
                                  disabled={Number(role.status) !== 1}
                                  onChange={(e) =>
                                    handleRoleChange(printer.id, role, {
                                      number_of_print: Math.max(1, Number(e.target.value) || 1),
                                    })
                                  }
                                />
                              </span>
                              <ToggleSwitch
                                large
                                checked={Number(role.status) === 1}
                                onChange={(e) =>
                                  handleRoleChange(printer.id, role, {
                                    status: e.target.checked ? 1 : 0,
                                  })
                                }
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <p style={{ opacity: 0.7 }}>Failed to load details.</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PrintersPanel;
